import { ChangeDetectorRef, ElementRef } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormGroup } from '@angular/forms';

import { WITHOUT_EMIT_EVENT } from '@jct/core';
import { AppLayoutService } from '@jct/ui';

import { scrollRegistrationEntryEditorIntoView } from './mobile-entry-editor';

/**
 * Per-page rules the editor cannot infer. Every fill-in table stores "this row was
 * filled in" differently (a name, a sentinel `''`, an id of 0, a `newRow` flag), so
 * the predicates and the row lifecycle stay with the page.
 */
export interface RegistrationRowsEditorPolicy {
  /** A row the user already filled in — it renders as a collapsed card. */
  isFilled(row: AbstractControl): boolean;

  /** Appends the trailing empty row unless one already exists. */
  addEmptyRow(): void;

  /** The page's own remove handler. It must call `onRowRemoved`. */
  removeRow(index: number): void;

  /** Clears a row back to its empty state, including re-disabling controls. */
  resetRow(row: UntypedFormGroup): void;

  /** Row-level validation for the mobile editor's Save button. */
  isRowValid(row: UntypedFormGroup): boolean;

  /**
   * A row that counts towards "the list has content". Defaults to `isFilled`.
   * Relatives distinguishes the two: a row can be non-new but still incomplete.
   */
  isCompleted?(row: AbstractControl): boolean;

  /** The row the editor should open on. Defaults to the first non-`isFilled` row. */
  isEmpty?(row: AbstractControl): boolean;

  /** Blocks Add — study track cannot add a track before an institute is picked. */
  canAdd?(): boolean;

  /** Runs before the editor opens, while row option lists may still be stale. */
  onEditorOpened?(): void;

  /** Runs after a row passed validation, before the editor collapses. */
  onRowSaved?(row: UntypedFormGroup): void;

  /** Runs after Cancel restored a snapshot — re-apply dependent validators here. */
  onSnapshotRestored?(row: UntypedFormGroup, snapshot: any): void;
}

/**
 * Mobile add/edit state for a fill-in `FormArray`: which row the editor is open on,
 * whether it is an add or an in-place edit, and how Save/Cancel/Delete move between
 * those states. The desktop `mat-table` is unaffected.
 */
export class RegistrationRowsEditor {
  editingRowIndex: number | null = null;
  invalid = false;

  private _snapshot: any = null;

  constructor(
    private rows: UntypedFormArray,
    private appLayout: AppLayoutService,
    private host: ElementRef<HTMLElement>,
    private cdr: ChangeDetectorRef,
    private policy: RegistrationRowsEditorPolicy,
  ) {}

  /** Desktop keeps a trailing empty row; mobile opens an editor only when empty. */
  initialize() {
    if (!this.appLayout.isMobile) {
      this.policy.addEmptyRow();
      return;
    }

    if (this.completedRows.length === 0) {
      this.policy.addEmptyRow();
      this.openEditor();
    }
  }

  get completedRows(): AbstractControl[] {
    return this.rows.controls.filter(row => this._isCompleted(row));
  }

  get showAddButton() {
    if (!this.appLayout.isMobile) {
      return false;
    }

    if (this.editingRowIndex === null) {
      return true;
    }

    const row = this.rows.at(this.editingRowIndex);

    return row ? this.isEditingExistingRow(row) : false;
  }

  isRowEditing(row: AbstractControl) {
    return this._rowIndex(row) === this.editingRowIndex;
  }

  isAddingRow(row: AbstractControl) {
    return this.isRowEditing(row) && !this._snapshot;
  }

  isEditingExistingRow(row: AbstractControl) {
    return this.isRowEditing(row) && !!this._snapshot;
  }

  isRowCollapsed(row: AbstractControl) {
    return this.policy.isFilled(row) && this._rowIndex(row) !== this.editingRowIndex;
  }

  startAdd() {
    if (this.editingRowIndex !== null) {
      return;
    }

    if (this.policy.canAdd && !this.policy.canAdd()) {
      return;
    }

    this.policy.addEmptyRow();
    this.openEditor();
    this.scrollIntoView();
  }

  startEdit(row: AbstractControl) {
    this._discardPendingAdd(row);

    if (this.policy.onEditorOpened) {
      this.policy.onEditorOpened();
    }

    this._snapshot = { ...row.getRawValue() };
    this.editingRowIndex = this._rowIndex(row);
    this.invalid = false;
    this.scrollIntoView();
  }

  save(row: AbstractControl) {
    const group = row as UntypedFormGroup;

    group.markAllAsTouched();

    if (!this.policy.isRowValid(group)) {
      this.invalid = true;
      return;
    }

    if (this.policy.onRowSaved) {
      this.policy.onRowSaved(group);
    }

    this._close();
  }

  cancel(row: AbstractControl) {
    const group = row as UntypedFormGroup;

    if (this._snapshot) {
      const snapshot = this._snapshot;

      // Silent restore: several pages clear a dependent field (institution name,
      // relative flags) from a `valueChanges` subscription on the parent field.
      group.patchValue(snapshot, WITHOUT_EMIT_EVENT);

      if (this.policy.onSnapshotRestored) {
        this.policy.onSnapshotRestored(group, snapshot);
      }

      this._close();
      return;
    }

    // Cancelling the only editor empties it instead of leaving the list blank.
    if (this.completedRows.length === 0) {
      this.policy.resetRow(group);
      this.invalid = false;
      return;
    }

    this.policy.removeRow(this._rowIndex(group));
    this._snapshot = null;
    this.invalid = false;
  }

  /** Keeps `editingRowIndex` aligned after the page removed a row. */
  onRowRemoved(index: number) {
    if (this.editingRowIndex === index) {
      this.editingRowIndex = null;
      this._snapshot = null;
    } else if (this.editingRowIndex !== null && this.editingRowIndex > index) {
      this.editingRowIndex -= 1;
    }

    this.ensureEditor();
  }

  /** Reopens an empty editor once the list has no content left. */
  ensureEditor() {
    if (!this.appLayout.isMobile) {
      return;
    }

    if (this.completedRows.length === 0 && this.editingRowIndex === null) {
      this.policy.addEmptyRow();
      this.openEditor();
    }
  }

  openEditor() {
    if (!this.appLayout.isMobile) {
      this.editingRowIndex = null;
      return;
    }

    if (this.policy.onEditorOpened) {
      this.policy.onEditorOpened();
    }

    const index = this.rows.controls.findIndex(row => this._isEmpty(row));

    this.editingRowIndex = index >= 0 ? index : 0;
    this._snapshot = null;
    this.invalid = false;
  }

  scrollIntoView() {
    scrollRegistrationEntryEditorIntoView(this.host, this.cdr, this.appLayout);
  }

  /** Switching from an untouched add editor straight to a card drops the add row. */
  private _discardPendingAdd(target: AbstractControl) {
    if (this.editingRowIndex === null || this._snapshot) {
      return;
    }

    const pending = this.rows.at(this.editingRowIndex);

    if (pending && pending !== target && !this.policy.isFilled(pending)) {
      this.policy.removeRow(this.editingRowIndex);
    }
  }

  private _close() {
    this.editingRowIndex = null;
    this._snapshot = null;
    this.invalid = false;
  }

  private _rowIndex(row: AbstractControl) {
    return this.rows.controls.indexOf(row);
  }

  private _isCompleted(row: AbstractControl) {
    return this.policy.isCompleted ? this.policy.isCompleted(row) : this.policy.isFilled(row);
  }

  private _isEmpty(row: AbstractControl) {
    return this.policy.isEmpty ? this.policy.isEmpty(row) : !this.policy.isFilled(row);
  }
}
